import React, { useState, useEffect } from 'react';
import { Sparkles, Timer, Flame, ArrowRight, CheckCircle2, RotateCcw, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { quiz, flashDrop } from '../mock';
import { API } from '../lib/api';
import { toast } from 'sonner';

// ---------- Quiz / Assessment Block ----------
export function QuizBlock({ onSelectProduct, products = [], handle = 'aria.mercer' }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [email, setEmail] = useState('');
  const [leadDone, setLeadDone] = useState(false);
  const [leadDismissed, setLeadDismissed] = useState(false);

  const handleAnswer = (value) => { setAnswers([...answers, value]); setStep(step + 1); };
  const reset = () => { setStep(0); setAnswers([]); setLeadDone(false); setLeadDismissed(false); setEmail(''); };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await API.post('/leads', { handle, email, source: 'quiz', context: 'Quiz result' });
      setLeadDone(true);
      toast.success('Sent! Check your inbox for your custom plan.');
    } catch { toast.error('Could not save email'); }
  };

  const done = step >= quiz.questions.length;
  const recIndex = done ? quiz.recommend(answers[0], answers[1]) : null;
  const rec = done ? (products[recIndex] || products[0]) : null;

  return (
    <div className="glass animate-fade-up relative overflow-hidden rounded-2xl p-5">
      <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15"><Sparkles className="h-4 w-4 text-primary" /></span>
        <div>
          <h3 className="font-display text-sm font-semibold text-white">{quiz.title}</h3>
          <p className="text-[11px] text-muted-foreground">{quiz.subtitle}</p>
        </div>
        <span className="ml-auto rounded-full bg-white/8 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">Assessment</span>
      </div>

      {!done && (
        <div key={step} className="animate-fade-up">
          <div className="mb-3 flex gap-1.5">
            {quiz.questions.map((_, i) => (<div key={i} className={`h-1 flex-1 rounded-full transition-luxe ${i <= step ? 'bg-primary' : 'bg-white/10'}`} />))}
          </div>
          <p className="mb-3 font-display text-[15px] font-medium text-white">{quiz.questions[step].prompt}</p>
          <div className="grid gap-2">
            {quiz.questions[step].options.map((opt) => (
              <button key={opt.value} onClick={() => handleAnswer(opt.value)} className="transition-luxe flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white hover:border-primary/50 hover:bg-primary/[0.08]">
                {opt.label}<ArrowRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}

      {done && rec && (
        <div className="animate-fade-up">
          <div className="mb-3 flex items-center gap-2 text-primary"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-medium">Your perfect match</span></div>
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
            <img src={rec.image} alt={rec.title} className="h-14 w-14 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-sm font-semibold text-white">{rec.title}</p>
              <p className="text-xs text-muted-foreground">${rec.price} · {rec.type}</p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => onSelectProduct(rec)} className="flex-1 rounded-xl bg-primary font-semibold text-primary-foreground hover:bg-primary/90 glow-lime">Get this offer</Button>
            <Button onClick={reset} variant="ghost" className="rounded-xl text-muted-foreground hover:text-white"><RotateCcw className="h-4 w-4" /></Button>
          </div>

          {!leadDismissed && (leadDone ? (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-primary"><CheckCircle2 className="h-3.5 w-3.5" /> Your custom plan is on its way!</p>
          ) : (
            <form onSubmit={submitLead} className="mt-3 rounded-xl border border-primary/20 bg-primary/[0.06] p-2.5">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] text-white/75">Email me this plan + a bonus guide</span>
                <button type="button" onClick={() => setLeadDismissed(true)} className="text-white/40 hover:text-white"><X className="h-3 w-3" /></button>
              </div>
              <div className="flex gap-2">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="h-9 rounded-lg border-white/10 bg-white/[0.05] text-sm focus-visible:ring-primary" />
                <Button type="submit" size="icon" className="h-9 w-9 shrink-0 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"><ArrowRight className="h-4 w-4" /></Button>
              </div>
            </form>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Flash Drop Block ----------
function useCountdown(initial) {
  const [secs, setSecs] = useState(initial);
  useEffect(() => { const t = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000); return () => clearInterval(t); }, []);
  const h = String(Math.floor(secs / 3600)).padStart(2, '0');
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
  const s = String(secs % 60).padStart(2, '0');
  return { h, m, s };
}

export function FlashDropBlock({ onSelectProduct }) {
  const { h, m, s } = useCountdown(flashDrop.endsInSeconds);
  const dropProduct = {
    id: 'flash', type: 'Flash Drop', title: flashDrop.title, subtitle: flashDrop.subtitle,
    description: 'A one-time private intensive. Limited seats, limited time.',
    price: flashDrop.price, compareAt: flashDrop.compareAt,
    image: 'https://images.unsplash.com/photo-1526570207772-784d36084510',
    highlights: ['90-min private intensive', 'Custom scaling roadmap', 'Only 5 seats total', 'Lifetime call recording'],
    rating: 5.0, lookup_key: null,
  };

  return (
    <div className="animate-fade-up relative overflow-hidden rounded-2xl border border-primary/25 p-5" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(255,255,255,0.02))' }}>
      <div className="pointer-events-none absolute -bottom-10 -right-6 h-32 w-32 rounded-full bg-primary/20 blur-3xl" />
      <div className="mb-2 flex items-center gap-2">
        <span className="flex items-center gap-1 rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary"><Flame className="h-3 w-3" /> Flash Drop</span>
        <span className="ml-auto flex items-center gap-1 text-xs font-medium text-primary"><Timer className="h-3.5 w-3.5" /> {h}:{m}:{s}</span>
      </div>
      <h3 className="font-display text-lg font-bold text-white">{flashDrop.title}</h3>
      <p className="mt-0.5 text-sm text-muted-foreground">{flashDrop.subtitle}</p>
      <div className="mt-3 flex items-end gap-2">
        <span className="font-display text-2xl font-bold text-white">${flashDrop.price}</span>
        <span className="mb-1 text-sm text-muted-foreground line-through">${flashDrop.compareAt}</span>
        <span className="mb-1 ml-auto text-xs font-medium text-primary">{flashDrop.seatsLeft} seats left</span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-primary" style={{ width: `${(flashDrop.seatsLeft / 5) * 100}%` }} /></div>
      <Button onClick={() => onSelectProduct(dropProduct)} className="mt-4 w-full rounded-xl bg-primary font-semibold text-primary-foreground hover:bg-primary/90 glow-lime">Claim my seat</Button>
    </div>
  );
}
